import type { OpenAPI } from "simplydo/interfaces";
import type { ChallengeWithIdeaTemplate } from "simplydo/interfaces/types/challengeTypes";

export type IdeaTemplateBody = NonNullable<
  OpenAPI.Schemas["IdeaTemplate"]["body"]
>;

type BaseIdeaTemplateBodySection = IdeaTemplateBody[number];

type BaseIdeaTemplateField = NonNullable<
  BaseIdeaTemplateBodySection["fields"]
>[number];

export type IdeaTemplateFieldArrayKey = "options" | "columns";

export type IdeaTemplateAutoLabelConditions = {
  enabled?: boolean;
  conditionType?: "always" | "submitted";
  conditions?: {
    field?: string | null;
    condition?: string;
    value?: unknown;
    labels?: string[];
  }[];
};

/** Runtime/editor fields not fully covered by OpenAPI. */
export type IdeaTemplateField = Omit<
  BaseIdeaTemplateField,
  "minslider" | "maxslider" | "minValue" | "maxValue"
> & {
  _id?: string;
  hideSliders?: boolean;
  width?: string | number;
  minValue?: number | null;
  maxValue?: number | null;
  minslider?: number | null;
  maxslider?: number | null;
  visibility?: "manager" | null;
  autoLabelConditions?: IdeaTemplateAutoLabelConditions;
};

export type IdeaTemplateSection = Omit<
  BaseIdeaTemplateBodySection,
  "fields"
> & { sectionIndex?: number; fields: IdeaTemplateField[] };

/** Assessment scoring options object (distinct from string[] checkbox options). */
export type IdeaTemplateFieldScoringOptions = {
  availableScore?: number;
  notes?: string;
  weighting?: number;
};

export type IdeaTemplateAssessmentRound = Omit<
  NonNullable<
    NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
  >[number],
  "assessmentDeadline" | "assessmentClosed"
> & {
  assessmentDeadline?: string | Date | null;
  assessmentClosed?: boolean | null;
};

export type FormatEditorAssessments = {
  rounds?: IdeaTemplateAssessmentRound[];
};

export type FormatEditorChallenge = ChallengeWithIdeaTemplate & {
  creditsafeAvailable?: boolean;
  assessmentScoreProcessing?: boolean;
};

export type EditableIdeaTemplate = Omit<
  OpenAPI.Schemas["IdeaTemplate"],
  "body"
> & { body?: IdeaTemplateSection[] };

export type FormatEditorSettingsValue =
  | string
  | number
  | boolean
  | null
  | Date
  | FormatEditorAssessments
  | IdeaTemplateSection[];

export type FormatEditorOnSettingsUpdated = (
  key: string,
  value: FormatEditorSettingsValue,
) => void;

export const normalizeTemplateBody = (
  body: IdeaTemplateBody | IdeaTemplateSection[] | undefined,
): IdeaTemplateSection[] =>
  (body ?? []).map((section, index) => ({
    ...section,
    sectionIndex: (section as IdeaTemplateSection).sectionIndex ?? index,
    fields: (section.fields ?? []) as IdeaTemplateField[],
  }));

/** Strip editor-only sectionIndex before persisting to API cache. */
export const toApiTemplateBody = (
  sections: IdeaTemplateSection[],
): IdeaTemplateBody =>
  sections.map(({ sectionIndex: _sectionIndex, ...section }) => ({
    ...section,
    fields: section.fields as BaseIdeaTemplateBodySection["fields"],
  }));

export const toStoredIdeaTemplate = (
  template: EditableIdeaTemplate,
): OpenAPI.Schemas["IdeaTemplate"] => ({
  ...template,
  body: template.body ? toApiTemplateBody(template.body) : template.body,
});

export type ChallengeFormat =
  OpenAPI.GET<"/challenges/{id}/format">["response"];

/** The format query's shape: like GET /challenges/{id}/format, every section keeps its sectionIndex. */
export const toChallengeFormat = (
  template: EditableIdeaTemplate | OpenAPI.Schemas["IdeaTemplate"],
): ChallengeFormat => {
  const sections = normalizeTemplateBody(template.body);
  return {
    ...template,
    body: toApiTemplateBody(sections).map((section, index) => ({
      ...section,
      sectionIndex: sections[index]?.sectionIndex ?? index,
    })),
    assessments: {
      ...template.assessments,
      rounds: template.assessments?.rounds ?? [],
    },
    assessmentEnabled: template.assessmentEnabled ?? false,
  };
};

export const PREVIEW_IDEA_ID = "format-preview";

export const getFieldStringOptions = (field: IdeaTemplateField): string[] => {
  const { options } = field;
  return Array.isArray(options) ? options : [];
};

export const getFieldArrayValue = (
  field: IdeaTemplateField,
  fieldName: IdeaTemplateFieldArrayKey,
): unknown[] => {
  const value = field[fieldName];
  return Array.isArray(value) ? [...value] : [];
};

export const getScoringOptions = (
  options: IdeaTemplateField["options"],
): IdeaTemplateFieldScoringOptions | undefined => {
  if (!options || Array.isArray(options)) {
    return undefined;
  }
  return options;
};
